import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useCurrentOrganization } from './use-current-organization';

export interface Status {
  id: string;
  name: string;
  color?: string;
  organizationId: string;
  createdAt: string;
}

export interface Priority {
  id: string;
  name: string;
  color?: string;
  isDefault?: boolean;
  organizationId: string;
  createdAt: string;
}

/**
 * Hook to load organization-scoped metadata (status and priorities) once at app initialization.
 * These are cached globally with long staleTime since they rarely change.
 * 
 * This hook should be called at app level when user is authenticated to preload data.
 * Components can then use the cached data via React Query's shared cache.
 */
export function useOrganizationMetadata() {
  const { organizationId } = useCurrentOrganization();
  
  // Fetch status data - organization-scoped, cached globally
  const { data: statusResponse, isLoading: statusLoading, error: statusError } = useQuery<{ success: boolean; data: Status[] }>({
    queryKey: ["/api/status", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : "/api/status";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
    staleTime: 30 * 60 * 1000, // 30 minutes - status rarely changes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch priorities data - organization-scoped, cached globally
  // Note: Cache Priority[] directly (not wrapped) to match usePriorities hook structure
  const { data: prioritiesData, isLoading: prioritiesLoading, error: prioritiesError } = useQuery<Priority[]>({
    queryKey: ["/api/priorities", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/priorities?organizationId=${encodeURIComponent(organizationId)}` : "/api/priorities";
      const response = await apiRequest(url);
      const result = await response.json();
      return result?.data || [];
    },
    enabled: !!organizationId,
    staleTime: 30 * 60 * 1000, // 30 minutes - priorities rarely change
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const status = statusResponse?.data || [];
  const priorities = Array.isArray(prioritiesData) ? prioritiesData : [];
  const isLoading = statusLoading || prioritiesLoading;
  const error = statusError || prioritiesError;

  return {
    status,
    priorities,
    isLoading,
    error,
    hasData: !!organizationId && !isLoading && (status.length > 0 || priorities.length > 0),
  };
}

